import { supabase } from "./supabase-client";
import { formatBRL } from "./utils";
import { normalizePhoneDigits } from "./validators";

export interface CreditCardData {
  holderName: string;
  number: string;
  expiryMonth: string;
  expiryYear: string;
  ccv: string;
  postalCode?: string;
  addressNumber?: string;
}

export interface CreatePaymentResult {
  success: boolean;
  stub?: boolean;
  alreadyCreated?: boolean;
  paymentId?: string;
  pixQrCode?: string;
  pixQrCodeImage?: string;
  expiresAt?: string;
  boletoUrl?: string;
  boletoBarcode?: string;
  dueDate?: string;
  immediateConfirmation?: boolean;
  chargeStatus?: string;
  message?: string;
  error?: string;
}

/**
 * Calls the create-payment edge function to process payment via Asaas.
 * payerPhone/payerName are preferred over profiles when the checkout collected them.
 */
export const createPayment = async (
  orderId: string,
  paymentMethod: "pix" | "credit_card" | "boleto",
  creditCard?: CreditCardData,
  installments?: number,
  payerCpf?: string,
  payerDocumentNumber?: string,
  payerDocumentType?: "cpf" | "cnpj",
  companyName?: string,
  payerPhone?: string,
  payerName?: string,
): Promise<CreatePaymentResult> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();

    if (!session?.access_token) {
      return {
        success: false,
        error: "Sua sessão expirou. Faça login novamente para continuar o pagamento.",
      };
    }
    
    console.log("[createPayment] Calling edge function", { orderId, paymentMethod, hasSession: !!session });

    const payload = {
      orderId,
      paymentMethod,
      creditCard,
      installments,
      payerCpf,
      payerDocumentNumber,
      payerDocumentType,
      companyName,
      payerPhone: normalizePhoneDigits(payerPhone) || undefined,
      payerName: payerName?.trim() || undefined,
    };
    const invokePayment = () => supabase.functions.invoke("create-payment", {
      body: payload,
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    // A lost response does not establish whether the gateway created a charge.
    // Do not automatically replay payment until server-side concurrency is safe.
    const { data, error } = await invokePayment();

    if (error) {
      console.error("[createPayment] FunctionsInvokeError:", error);
      
      // Try to extract the actual error message from the response
      // supabase-js wraps non-2xx responses in FunctionsHttpError
      let errorMessage = error.message || "Erro ao processar pagamento";
      
      // The error.context might contain the response body
      if ((error as any).context) {
        try {
          const ctx = (error as any).context;
          if (ctx && typeof ctx.json === 'function') {
            const body = await ctx.json();
            console.error("[createPayment] Error response body:", JSON.stringify(body, null, 2));
            if (body?.code === "UNAUTHORIZED_UNSUPPORTED_TOKEN_ALGORITHM") {
              errorMessage = "Sessão de autenticação incompatível com o gateway de pagamento. Faça logout e entre novamente com e-mail e senha.";
            }
            if (body?.details?.[0]?.code === "invalid_access_token") {
              errorMessage = "Configuração de pagamento inválida no servidor. A chave de API do Asaas está incorreta. Entre em contato com o suporte.";
            }
            if (body?.userError) {
              errorMessage = body.userError;
            } else if (body?.error) {
              errorMessage = body.error;
            }
            if (body?.details?.[0]?.description && body?.details?.[0]?.code !== "invalid_access_token") {
              errorMessage += ` — ${body.details[0].description}`;
            }
          }
        } catch (parseErr) {
          console.error("[createPayment] Could not parse error context:", parseErr);
        }
      }
      
      return { success: false, error: errorMessage };
    }

    // Handle successful HTTP response but application-level error
    const parsedData = data as any;
    console.log("[createPayment] Response:", JSON.stringify(parsedData));
    
    if (parsedData && parsedData.success === false) {
      console.error("[createPayment] Backend Error:", parsedData);
      return { 
         success: false, 
         error: parsedData.userError 
           ? `Erro Auth: ${parsedData.userError} | Hrd: ${parsedData.headerDebug}` 
           : parsedData.error || "Unknown Edge Error"
      };
    }

    // Check for error field without success field (400/500 responses that somehow got through)
    if (parsedData && parsedData.error && !parsedData.success) {
      console.error("[createPayment] Error in response data:", parsedData);
      let msg = parsedData.error;
      if (parsedData.details?.[0]?.description) {
        msg += ` — ${parsedData.details[0].description}`;
      }
      return { success: false, error: msg };
    }

    return parsedData as CreatePaymentResult;
  } catch (unexpectedError: any) {
    console.error("[createPayment] Unexpected error:", unexpectedError);
    return { success: false, error: unexpectedError.message || "Erro inesperado ao processar pagamento" };
  }
};

export interface InstallmentOption {
  n: number;
  label: string;
  value: number;
  total: number;
  hasInterest: boolean;
}

/** Taxa mensal de juros do parcelamento no cartão (Price / tabela Price). */
export const CARD_INSTALLMENT_MONTHLY_RATE = 0.0199;

/** Valor mínimo por parcela exigido pela Asaas. */
export const MIN_INSTALLMENT_VALUE = 5;

/**
 * Calcula valor da parcela e total com juros (fórmula Price).
 * n=1 → à vista, sem juros. n>=2 → 1,99% a.m.
 * Mesma fórmula usada em create-payment (edge).
 */
export const calcCardInstallment = (
  principal: number,
  n: number,
  monthlyRate: number = CARD_INSTALLMENT_MONTHLY_RATE,
): { installmentValue: number; total: number; hasInterest: boolean } => {
  const amount = Number(principal) || 0;
  if (n <= 1) {
    return {
      installmentValue: Number(amount.toFixed(2)),
      total: Number(amount.toFixed(2)),
      hasInterest: false,
    };
  }
  const factor = Math.pow(1 + monthlyRate, n);
  const installmentValue = Number(
    ((amount * monthlyRate * factor) / (factor - 1)).toFixed(2),
  );
  return {
    installmentValue,
    total: Number((installmentValue * n).toFixed(2)),
    hasInterest: true,
  };
};

/**
 * Opções de parcelamento no cartão:
 * - 1x à vista sem juros
 * - 2x–12x com 1,99% a.m. (juros compostos, tabela Price)
 * - esconde n>1 se parcela < R$ 5 (limite Asaas)
 */
export const getInstallmentOptions = (total: number): InstallmentOption[] => {
  const options: InstallmentOption[] = [];

  for (let n = 1; n <= 12; n++) {
    const { installmentValue, total: totalWithInterest, hasInterest } =
      calcCardInstallment(total, n);

    // Asaas: valor mínimo por parcela R$ 5 — não oferecer opção inválida
    if (n > 1 && installmentValue < MIN_INSTALLMENT_VALUE) continue;

    options.push({
      n,
      label: hasInterest
        ? `${n}x de ${formatBRL(installmentValue)} (total ${formatBRL(totalWithInterest)})`
        : `À vista — ${formatBRL(installmentValue)} sem juros`,
      value: installmentValue,
      total: totalWithInterest,
      hasInterest,
    });
  }

  return options;
};

export const createProducerAccount = async (
  producerId: string
): Promise<{ success: boolean; walletId?: string; error?: string }> => {
  const { data, error } = await supabase.functions.invoke(
    "create-producer-account",
    { body: { producerId } }
  );

  if (error) {
    return { success: false, error: error.message };
  }

  return data;
};

export interface RefundOrderParams {
  orderId: string;
  amount?: number;
  reason?: string;
  ticketIds?: string[];
}

/**
 * Calls the refund-payment edge function to process a refund.
 */
export const refundOrder = async (
  params: RefundOrderParams
): Promise<{ success: boolean; message?: string; error?: string; details?: any }> => {
  try {
    console.log("[refundOrder] Starting refund:", JSON.stringify(params));
    const { data: { session } } = await supabase.auth.getSession();

    if (!session?.access_token) {
      return {
        success: false,
        error: "Sua sessão expirou. Faça login novamente.",
      };
    }

    const { data, error } = await supabase.functions.invoke("refund-payment", {
      body: params,
      headers: {
        Authorization: `Bearer ${session.access_token}`
      },
    });

    if (error) {
      console.error("[refundOrder] FunctionsInvokeError:", error);

      // Extract detailed error from Edge Function response body
      let errorMessage = error.message || "Erro ao processar reembolso";
      let errorDetails: any = null;

      if ((error as any).context) {
        try {
          const ctx = (error as any).context;
          if (ctx && typeof ctx.json === 'function') {
            const body = await ctx.json();
            console.error("[refundOrder] Error response body:", JSON.stringify(body, null, 2));
            errorDetails = body;
            if (body?.error) {
              errorMessage = body.error;
            }
            if (body?.details) {
              errorDetails = body.details;
            }
          } else if (ctx && typeof ctx.text === 'function') {
            const text = await ctx.text();
            console.error("[refundOrder] Error response text:", text);
          }
        } catch (parseErr) {
          console.error("[refundOrder] Could not parse error context:", parseErr);
        }
      }

      return { success: false, error: errorMessage, details: errorDetails };
    }

    console.log("[refundOrder] Response:", JSON.stringify(data));
    return data;
  } catch (unexpectedError: any) {
    console.error("[refundOrder] Unexpected error:", unexpectedError);
    return { success: false, error: unexpectedError.message || "Erro inesperado ao processar reembolso" };
  }
};
